import { type ReactNode, useLayoutEffect, useRef, useState } from 'react'

import {
  getOverflowWarning,
  MOBILE_BANNER_FONT_FAMILY,
  MOBILE_BANNER_SUBTITLE_FONT_SIZE,
  MOBILE_BANNER_SUBTITLE_LETTER_SPACING,
  MOBILE_BANNER_TEXT_WIDTH_NO_IMAGE,
  MOBILE_BANNER_TEXT_WIDTH_WITH_IMAGE,
  MOBILE_BANNER_TITLE_FONT_SIZE,
  MOBILE_BANNER_TITLE_LETTER_SPACING,
  MOBILE_SPLASH_FONT_FAMILY,
  MOBILE_SPLASH_LINE_HEIGHT,
  MOBILE_SPLASH_SUBTITLE_FONT_SIZE,
  MOBILE_SPLASH_SUBTITLE_LETTER_SPACING,
  MOBILE_SPLASH_TEXT_WIDTH,
  MOBILE_SPLASH_TITLE_FONT_SIZE,
  MOBILE_SPLASH_TITLE_LETTER_SPACING,
} from './brandingMobileLayout'

interface UseBrandTextOverflowParams {
  brandTitle: string
  brandSubtitle: string
  hasImage: boolean
}

interface UseBrandTextOverflowResult {
  titleWarning: string | null
  subtitleWarning: string | null
  /**
   * Render this into the tree to enable measurement. The nodes are
   * absolutely positioned offscreen with visibility:hidden (not display:none),
   * so scrollWidth/scrollHeight read the true content size.
   */
  measurementNodes: ReactNode
}

/**
 * Detects whether the branding title/subtitle will truncate in the mobile
 * banner or wrap on the mobile splash screen, by measuring hidden DOM nodes
 * that mirror the mobile font + width. Returns non-blocking warning copy
 * plus the JSX you need to render to enable measurement.
 */
export function useBrandTextOverflow({
  brandTitle,
  brandSubtitle,
  hasImage,
}: UseBrandTextOverflowParams): UseBrandTextOverflowResult {
  const bannerTitleRef = useRef<HTMLSpanElement>(null)
  const bannerSubtitleRef = useRef<HTMLSpanElement>(null)
  const splashTitleRef = useRef<HTMLDivElement>(null)
  const splashSubtitleRef = useRef<HTMLDivElement>(null)

  const [titleOverflow, setTitleOverflow] = useState({ banner: false, splash: false })
  const [subtitleOverflow, setSubtitleOverflow] = useState({ banner: false, splash: false })

  useLayoutEffect(() => {
    const bannerAvailWidth = hasImage ? MOBILE_BANNER_TEXT_WIDTH_WITH_IMAGE : MOBILE_BANNER_TEXT_WIDTH_NO_IMAGE
    // 1px tolerance for sub-pixel rounding.
    const TOLERANCE = 1

    // Splash measurement nodes are clamped to 1 line so any wrap trips this check.
    // Mobile allows 2 lines, but we warn at the first wrap so users can decide to shorten.
    const isWrapped = (el: HTMLElement | null) => (el ? el.scrollHeight > el.clientHeight + TOLERANCE : false)

    const measure = () => {
      const tBanner = bannerTitleRef.current
      setTitleOverflow({
        banner: tBanner ? tBanner.scrollWidth > bannerAvailWidth + TOLERANCE : false,
        splash: isWrapped(splashTitleRef.current),
      })

      const sBanner = bannerSubtitleRef.current
      setSubtitleOverflow({
        banner: sBanner ? sBanner.scrollWidth > bannerAvailWidth + TOLERANCE : false,
        splash: isWrapped(splashSubtitleRef.current),
      })
    }

    measure()

    // Re-measure once Figtree loads -- cold cache uses the fallback font first.
    let cancelled = false
    if (typeof document !== 'undefined' && document.fonts && document.fonts.status !== 'loaded') {
      document.fonts.ready
        .then(() => {
          if (!cancelled) measure()
        })
        .catch(() => {
          /* font load failures are non-fatal */
        })
    }
    return () => {
      cancelled = true
    }
  }, [brandTitle, brandSubtitle, hasImage])

  const measurementNodes = (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        top: -9999,
        left: -9999,
        visibility: 'hidden',
        pointerEvents: 'none',
      }}>
      <span
        ref={bannerTitleRef}
        style={{
          fontSize: MOBILE_BANNER_TITLE_FONT_SIZE,
          fontFamily: MOBILE_BANNER_FONT_FAMILY,
          fontWeight: 600,
          letterSpacing: MOBILE_BANNER_TITLE_LETTER_SPACING,
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}>
        {brandTitle}
      </span>
      <span
        ref={bannerSubtitleRef}
        style={{
          fontSize: MOBILE_BANNER_SUBTITLE_FONT_SIZE,
          fontFamily: MOBILE_BANNER_FONT_FAMILY,
          fontWeight: 400,
          letterSpacing: MOBILE_BANNER_SUBTITLE_LETTER_SPACING,
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}>
        {brandSubtitle}
      </span>
      <div
        ref={splashTitleRef}
        style={{
          width: MOBILE_SPLASH_TEXT_WIDTH,
          fontSize: MOBILE_SPLASH_TITLE_FONT_SIZE,
          fontFamily: MOBILE_SPLASH_FONT_FAMILY,
          fontWeight: 600,
          letterSpacing: MOBILE_SPLASH_TITLE_LETTER_SPACING,
          textTransform: 'uppercase',
          lineHeight: MOBILE_SPLASH_LINE_HEIGHT,
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 1,
          overflow: 'hidden',
        }}>
        {brandTitle}
      </div>
      <div
        ref={splashSubtitleRef}
        style={{
          width: MOBILE_SPLASH_TEXT_WIDTH,
          fontSize: MOBILE_SPLASH_SUBTITLE_FONT_SIZE,
          fontFamily: MOBILE_SPLASH_FONT_FAMILY,
          fontWeight: 400,
          letterSpacing: MOBILE_SPLASH_SUBTITLE_LETTER_SPACING,
          textTransform: 'uppercase',
          lineHeight: MOBILE_SPLASH_LINE_HEIGHT,
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 1,
          overflow: 'hidden',
        }}>
        {brandSubtitle}
      </div>
    </div>
  )

  return {
    titleWarning: getOverflowWarning(titleOverflow, 'name'),
    subtitleWarning: getOverflowWarning(subtitleOverflow, 'subtitle'),
    measurementNodes,
  }
}
